import React, { useEffect, useState } from "react";
import { Form, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
	setIsLoggedIn,
	clearAccessibility,
	UserRole,
	nullAccessibility,
	setAccessibility,
	setDistrict,
	setSchool,
	setUser,
	clearCourse,
	isAdminRole,
} from "../../app/userSlice";
import { isString } from "../../app/utils";
import { applyAccessibility } from "../accessibility/accessibilityUtil";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { clearDismissedBroadcastAlerts } from "../../components/BroadcastAlerts";
import Password from "../../components/input/Password";
import SwitchToLegacy from "../../components/SwitchToLegacy";
import { useGetSelfMutation } from "../users/usersApiSlice";
import { useMakeDistrictSelectionMutation } from "../districts/districtsApiSlice";
import { useMakeSchoolSelectionMutation } from "../schools/schoolsApiSlice";
import { AccountLoginViewModel, useLoginMutation } from "./loginApiSlice";
import { LoginWithOutsideSiteButton } from "./LoginWithOutsideSiteButton";

export default function Login() {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [loginError, setLoginError] = useState("");
	const [isFormComplete, setIsFormComplete] = useState(false);

	const { aspUrl, config } = useAppSelector((state) => state.site);
	const { userId: loggedInUserId, isLoggedIn } = useAppSelector((state) => state.user);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [callLogin, loginResult] = useLoginMutation();
	const [callGetSelf] = useGetSelfMutation();
	const [makeDistrictSelection] = useMakeDistrictSelectionMutation();
	const [makeSchoolSelection] = useMakeSchoolSelectionMutation();

	const from = (location.state as any)?.from.pathname as string;

	// clean up the local storage prefersSpa flag that was used in 2024-2025.
	localStorage.removeItem("prefersSpa");

	if (config && !config.enableSinglePageApp) {
		sessionStorage.setItem("prefersSpa", "false");
		window.location.href = `${aspUrl}/Account/Login?returnUrl=`;
	}

	if (
		sessionStorage.getItem("prefersSpa") === "false" &&
		!isLoggedIn &&
		(!loginResult.isLoading || !loginResult.isSuccess)
	) {
		window.location.href = `${aspUrl}/Account/Login?returnUrl=`;
	}

	useEffect(() => {
		if (sessionStorage.getItem("isLegacyLoggedIn") === "true") {
			sessionStorage.removeItem("isLegacyLoggedIn");
			handleSuccessfulLogin();
			return;
		}
		dispatch(clearAccessibility());
	}, []);

	useEffect(() => {
		setIsFormComplete(!!username && !!password);
	}, [username, password]);

	const handleUsernameInput = (e: React.FormEvent<HTMLInputElement>) => {
		setUsername(e.currentTarget.value);
	};

	const handlePasswordInput = (e: React.FormEvent<HTMLInputElement>) => {
		setPassword(e.currentTarget.value);
	};

	const handleLogin = (event: React.FormEvent) => {
		event.preventDefault();

		clearDismissedBroadcastAlerts();

		setIsFormComplete(false);
		const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
		const loginModel: AccountLoginViewModel = {
			username,
			password,
			timezone: timeZone,
		};
		callLogin(loginModel)
			.unwrap()
			.then(() => {
				handleSuccessfulLogin();
			})
			.catch((error) => handleLoginError(error));
	};

	const handleSuccessfulLogin = () => {
		callGetSelf()
			.unwrap()
			.then((payload) => {
				dispatch(setIsLoggedIn(true));

				if (payload.userId !== loggedInUserId) {
					dispatch(setUser({ ...payload }));
					dispatch(
						setAccessibility(
							payload.accessibility
								? { ...payload.accessibility }
								: { ...nullAccessibility }
						)
					);
					dispatch(clearCourse());
					dispatch(setSchool(payload.school));
					dispatch(setDistrict(payload.district));
					applyAccessibility(payload.accessibility);

					//call to set userSession values for each of the below
					if (payload.role === UserRole.DistrictAdmin && payload.district) {
						makeDistrictSelection(payload.district.id)
							.unwrap()
							.catch((error: any) => {
								const e = isString(error) ? error : JSON.stringify(error);
								console.log(`error calling MakeDistrictSelection, error = ${e}`);
							});
					}
					if (payload.role === UserRole.SchoolAdmin && payload.school) {
						makeSchoolSelection(payload.school.id)
							.unwrap()
							.catch((error: any) => {
								const e = isString(error) ? error : JSON.stringify(error);
								console.log(`error calling MakeSchoolSelection, error = ${e}`);
							});
					}
				}

				if (!payload.bypassTermsOfService && !payload.isImpersonating) {
					navigate("/termsOfService");
				} else if (payload.requiresAccountConfirmation && !payload.isImpersonating) {
					navigate("/myAccount");
				} else if (from) {
					navigate(from);
				} else if (payload.role == UserRole.Writer || payload.role == UserRole.Proctor) {
					navigate("/courses");
				} else if (payload.role == UserRole.SchoolAdmin && !payload.school) {
					navigate("/selectSchool");
				} else if (payload.role == UserRole.DistrictAdmin && !payload.district) {
					navigate("/selectDistrict");
				} else {
					isAdminRole(payload.role) ? navigate("/adminHome") : navigate("/home");
				}
			})
			.catch((error: any) => {
				const e = isString(error) ? error : JSON.stringify(error);
				console.log(`error calling GetSelf, error = ${e}`);
			});
	};

	const handleLoginError = (error: string) => {
		setLoginError(error);
		setIsFormComplete(false);
	};

	const handleClearError = () => {
		setLoginError("");
	};

	return (
		<main>
			<section className="mt-16 mb-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className="bg-primary-50 py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
					<h2 className="mb-8 text-2xl font-bold tracking-tight text-gray-600">
						Sign into your account
					</h2>
					<Form
						id="login-form"
						className="space-y-6"
						onSubmit={handleLogin}
						method="post">
						<AlertMessage
							className="mt-8"
							type={AlertLevel.Error}
							message={loginError}
							onClear={handleClearError}
						/>

						<div>
							<label
								htmlFor="username"
								className="block text-sm font-medium text-gray-700">
								Username
							</label>
							<div className="relative mt-1 rounded-md shadow-sm">
								<input
									type="text"
									id="username"
									name="username"
									className="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
									placeholder="username"
									required
									autoComplete="username"
									onInput={(e) => handleUsernameInput(e)}
									value={username}
								/>
							</div>
						</div>

						<div>
							<label
								htmlFor="password"
								className="block text-sm font-medium text-gray-700">
								Password
							</label>
							<div className="mt-1 rounded-md shadow-sm">
								<Password
									id="password"
									name="password"
									className="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
									onInput={(e) => handlePasswordInput(e)}
									required={true}
									value={password}
								/>
							</div>
						</div>

						<div className="flex justify-between items-end pt-4">
							<button
								type="submit"
								id="sign-in-button"
								className="btn btn-primary"
								disabled={!isFormComplete}>
								Sign In
							</button>
							<a
								onClick={() => navigate("/forgotPassword")}
								id="forgot-password-link"
								className={`${config?.isUtahComposeSite ? "!text-[var(--link-text)]" : ""} btn btn-naked`}>
								Forgot your password?
							</a>
						</div>
						{config?.isUtahComposeSite && config?.allowProctorCreation && (
							<section className="-mx-2">
								<div className="grow border-t border-gray-400"></div>
								<div className="text-center mt-4">
									<p className="text-lg">Don’t have an account?</p>
									<a
										className="mt-1 text-sm font-medium link no-underline"
										onClick={() => navigate("/register")}>
										Register for your account here using your CACTUS ID
									</a>
									<p className="mt-4 text-sm text-gray-700 font-medium">
										No CACTUS ID? See your School Administrator to request
										access.
									</p>
								</div>
							</section>
						)}
					</Form>
				</div>
			</section>
			{config?.enableExternalAuth && (
				<section>
					<div className="relative flex items-center sm:mx-auto sm:w-full sm:max-w-md">
						<div className="grow border-t border-gray-400"></div>
						<span className="shrink mx-4 text-gray-400">or</span>
						<div className="grow border-t border-gray-400"></div>
					</div>
					{config?.classLinkClientId && (
						<section className="flex justify-center pt-8 sm:mx-auto sm:w-full sm:max-w-md">
							<LoginWithOutsideSiteButton
								id="classLink-button"
								siteName="ClassLink"
								onSuccessfulLogin={handleSuccessfulLogin}
								onLoginError={handleLoginError}
							/>
						</section>
					)}
					{config?.cleverClientId && (
						<section className="flex justify-center pt-6 sm:mx-auto sm:w-full sm:max-w-md">
							<LoginWithOutsideSiteButton
								id="clever-button"
								siteName="Clever"
								onSuccessfulLogin={handleSuccessfulLogin}
								onLoginError={handleLoginError}
							/>
						</section>
					)}
				</section>
			)}
			{config?.enableNotYetACustomer && (
				<section>
					<div className="relative flex pt-8 items-center sm:mx-auto sm:w-full sm:max-w-md">
						<div className="grow border-t border-gray-400"></div>
						<span className="shrink mx-4 text-gray-400">or</span>
						<div className="grow border-t border-gray-400"></div>
					</div>
					<section className="min-h-full justify-center pt-8 sm:px-6 lg:px-8">
						<div className="text-center">
							<a
								href={config?.notYetACustomerLink}
								id="not-yet-a-customer-link"
								className="text-lg text-primary-700 hover:text-primary-600 text-center">
								Not yet a customer?
							</a>
						</div>
					</section>
				</section>
			)}
		</main>
	);
}
